import { Dispatch, Reducer } from 'react';

import { Action } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';

export const initialState: IState = {
  errorNearby: null,
  errorFavs: null,
  errorRecent: null,
  searchingNearby: false,
  searchingRecent: false,
  searchingFavs: false,
  storesNearby: undefined,
  storesFavs: undefined,
  storesRecent: undefined,
  activeStoreId: '',
  rawIsSearching: undefined,
  storesById: {},
};

export enum ActionTypes {
  SEARCH_NEARBY_REQUESTED = 'SEARCH_NEARBY_REQUESTED',
  SEARCH_NEARBY_SUCCESSFUL = 'SEARCH_NEARBY_SUCCESSFUL',
  SEARCH_FAVS_SUCCESSFUL = 'SEARCH_FAVS_SUCCESSFUL',
  SEARCH_RECENT_SUCCESSFUL = 'SEARCH_RECENT_SUCCESSFUL',
  SEARCH_NEARBY_ERROR = 'SEARCH_NEARBY_ERROR',
  SEARCH_FAVS_ERROR = 'SEARCH_FAVS_ERROR',
  SEARCH_RECENT_ERROR = 'SEARCH_RECENT_ERROR',
  SET_ACTIVE_STORE_ID = 'SET_ACTIVE_STORE_ID',
  SEARCH_RECENT_REQUESTED = 'SEARCH_RECENT_REQUESTED',
  SEARCH_FAV_REQUESTED = 'SEARCH_FAV_REQUESTED',
}

export const searchNearbyRequested = (): Action<ActionTypes.SEARCH_NEARBY_REQUESTED> => ({
  type: ActionTypes.SEARCH_NEARBY_REQUESTED,
});

export const searchRecentRequested = (): Action<ActionTypes.SEARCH_RECENT_REQUESTED> => ({
  type: ActionTypes.SEARCH_RECENT_REQUESTED,
});

export const searchFavRequested = (): Action<ActionTypes.SEARCH_FAV_REQUESTED> => ({
  type: ActionTypes.SEARCH_FAV_REQUESTED,
});

export const searchNearbySuccess = (
  result: readonly IRestaurant[]
): Action<ActionTypes.SEARCH_NEARBY_SUCCESSFUL, readonly IRestaurant[]> => ({
  payload: result,
  type: ActionTypes.SEARCH_NEARBY_SUCCESSFUL,
});

export const searchFavsSuccess = (
  result: readonly IRestaurant[]
): Action<ActionTypes.SEARCH_FAVS_SUCCESSFUL, readonly IRestaurant[]> => ({
  payload: result,
  type: ActionTypes.SEARCH_FAVS_SUCCESSFUL,
});

export const searchRecentSuccess = (
  result: readonly IRestaurant[]
): Action<ActionTypes.SEARCH_RECENT_SUCCESSFUL, readonly IRestaurant[]> => ({
  payload: result,
  type: ActionTypes.SEARCH_RECENT_SUCCESSFUL,
});

export const searchNearbyError = (
  error: Error
): Action<ActionTypes.SEARCH_NEARBY_ERROR, Error> => ({
  error: true,
  payload: error,
  type: ActionTypes.SEARCH_NEARBY_ERROR,
});

export const searchFavsError = (error: Error): Action<ActionTypes.SEARCH_FAVS_ERROR, Error> => ({
  error: true,
  payload: error,
  type: ActionTypes.SEARCH_FAVS_ERROR,
});

export const searchRecentError = (
  error: Error
): Action<ActionTypes.SEARCH_RECENT_ERROR, Error> => ({
  error: true,
  payload: error,
  type: ActionTypes.SEARCH_RECENT_ERROR,
});

export const setActiveStoreId = (
  storeId: string
): Action<ActionTypes.SET_ACTIVE_STORE_ID, string> => ({
  payload: storeId,
  type: ActionTypes.SET_ACTIVE_STORE_ID,
});

export type IStore = Pick<
  IRestaurant,
  | '_id'
  | 'isFavorite'
  | 'latitude'
  | 'longitude'
  | 'diningRoomHours'
  | 'driveThruHours'
  | 'mobileOrderDriveThruHours'
  | 'hasMobileOrdering'
  | 'number'
  | 'mobileOrderingStatus'
  | 'localTime'
  | 'serviceModeHours'
  | 'dayPartHours'
> & {
  country: string;
  address: string;
  distance?: number;
};

export interface IState {
  errorNearby: null | Error;
  errorFavs: null | Error;
  errorRecent: null | Error;
  searchingNearby: boolean;
  searchingRecent: boolean;
  searchingFavs: boolean;
  storesNearby?: IStore[];
  storesFavs?: IStore[];
  storesRecent?: IStore[];
  activeStoreId: string;
  storesById: Record<string, IRestaurant>;
  // above searchings have some falsy fallback logic that screws with performance measurements
  // not changing the above due to un-forseen UI consequences of doing that
  rawIsSearching?: boolean;
}

/**
 * Maps an IRestaurant to IStore, selecting only the properties needed for the store locator.
 * This reduces the memory footprint and improves performance by creating a lighter object
 * with just the essential data, instead of using the full IRestaurant object.
 */
export const mapStoreResponse = (store: IRestaurant): IStore => ({
  _id: store._id,
  country: store.physicalAddress?.country ?? '',
  isFavorite: store.isFavorite,
  latitude: store.latitude,
  longitude: store.longitude,
  diningRoomHours: store.diningRoomHours,
  driveThruHours: store.driveThruHours,
  hasMobileOrdering: store.hasMobileOrdering,
  number: store.number,
  address: store.physicalAddress?.address1 ?? '',
  mobileOrderingStatus: store.mobileOrderingStatus,
  mobileOrderDriveThruHours: store.mobileOrderDriveThruHours,
  localTime: store.localTime,
  serviceModeHours: store.serviceModeHours,
  dayPartHours: store.dayPartHours,
});

export const mapStoresResponse = (stores: IRestaurant[]): IStore[] => stores.map(mapStoreResponse);

type Actions = ReturnType<
  | typeof searchNearbyRequested
  | typeof searchNearbySuccess
  | typeof searchNearbyError
  | typeof searchFavsError
  | typeof searchRecentError
  | typeof searchFavsSuccess
  | typeof searchRecentSuccess
  | typeof setActiveStoreId
  | typeof searchRecentRequested
  | typeof searchFavRequested
>;

export type StoreAction = Dispatch<Actions>;

const reducer: Reducer<IState, Actions> = (state = initialState, action: Actions) => {
  switch (action.type) {
    case ActionTypes.SEARCH_NEARBY_ERROR:
      return {
        ...state,
        errorNearby: action.payload,
        storesNearby: [],
        searchingNearby: false,
      } as IState;
    case ActionTypes.SEARCH_FAVS_ERROR:
      return {
        ...state,
        errorFavs: action.payload,
        storesFavs: [],
        searchingFavs: false,
      };
    case ActionTypes.SEARCH_RECENT_ERROR:
      return {
        ...state,
        errorRecent: action.payload,
        storesRecent: [],
        searchingRecent: false,
      };
    case ActionTypes.SEARCH_NEARBY_REQUESTED:
      return {
        ...state,
        searchingNearby: true,
      };
    case ActionTypes.SEARCH_RECENT_REQUESTED:
      return { ...state, searchingRecent: true };
    case ActionTypes.SEARCH_FAV_REQUESTED:
      return { ...state, searchingFavs: true };
    case ActionTypes.SEARCH_NEARBY_SUCCESSFUL: {
      const storesNearby = mapStoresResponse([...action.payload]);
      const storesById = action.payload.reduce((acc, store) => {
        if (store._id) {
          acc[store._id] = store;
        }
        return acc;
      }, {} as Record<string, IRestaurant>);

      return {
        ...state,
        storesNearby: [...storesNearby],
        storesById: { ...state.storesById, ...storesById },
        searchingNearby: false,
      };
    }
    case ActionTypes.SEARCH_FAVS_SUCCESSFUL: {
      const storesFavs = mapStoresResponse([...action.payload]);
      const storesById = action.payload.reduce((acc, store) => {
        if (store._id) {
          acc[store._id] = store;
        }
        return acc;
      }, {} as Record<string, IRestaurant>);

      return {
        ...state,
        storesFavs: [...storesFavs],
        storesById: { ...state.storesById, ...storesById },
        searchingFavs: false,
      };
    }
    case ActionTypes.SEARCH_RECENT_SUCCESSFUL: {
      const storesRecent = mapStoresResponse([...action.payload]);

      const storesById = action.payload.reduce((acc, store) => {
        if (store._id) {
          acc[store._id] = store;
        }
        return acc;
      }, {} as Record<string, IRestaurant>);

      return {
        ...state,
        storesRecent: [...storesRecent],
        storesById: { ...state.storesById, ...storesById },
        searchingRecent: false,
      };
    }
    case ActionTypes.SET_ACTIVE_STORE_ID:
      return {
        ...state,
        activeStoreId: action.payload,
      };
    default:
      return state;
  }
};

export default reducer;
